import { z } from 'zod';

export const hqHalfyearMetricCatalog = [
  {
    id: 'risk.items',
    label: '위험성평가 평가 항목',
    unit: '항목',
    feature: 'regular-risk',
    module: 'risk',
    field: 'annualItemCount',
    completeness: 'annualItemCountComplete',
    period: 'annual',
  },
  {
    id: 'risk.plans',
    label: '위험성평가 감소대책',
    unit: '건',
    feature: 'regular-risk',
    module: 'risk',
    field: 'annualReductionPlannedCount',
    completeness: 'annualReductionPlannedComplete',
    period: 'annual',
  },
  {
    id: 'risk.actions',
    label: '위험성평가 조치 결과',
    unit: '건',
    feature: 'regular-risk',
    module: 'risk',
    field: 'annualActionResultCount',
    completeness: 'annualActionResultComplete',
    period: 'annual',
  },
  {
    id: 'inspection.findings',
    label: '월 점검 지적',
    unit: '건',
    feature: 'inspection-reporting',
    module: 'inspection',
    field: 'currentMonthFindings',
    completeness: null,
    period: 'monthly',
  },
  {
    id: 'inspection.actions',
    label: '월 점검 지적 조치 완료',
    unit: '건',
    feature: 'inspection-reporting',
    module: 'inspection',
    field: 'currentMonthCompleted',
    completeness: null,
    period: 'monthly',
  },
  {
    id: 'education.records',
    label: '월 교육 실행 기록',
    unit: '건',
    feature: 'safety-training',
    module: 'education',
    field: 'total',
    completeness: null,
    period: 'monthly',
  },
  {
    id: 'education.attendance',
    label: '월 교육 참석 인원 합계',
    unit: '명',
    feature: 'safety-training',
    module: 'education',
    field: 'attendance',
    completeness: null,
    period: 'monthly',
  },
  {
    id: 'drill.completed',
    label: '월 비상훈련 결과',
    unit: '건',
    feature: 'evacuation-drill',
    module: 'drill',
    field: 'completed',
    completeness: null,
    period: 'monthly',
  },
] as const;
export const hqHalfyearMetricIdSchema = z.enum(hqHalfyearMetricCatalog.map((row) => row.id));
export type HqHalfyearMetricId = z.infer<typeof hqHalfyearMetricIdSchema>;
export const hqHalfyearTopics = [
  { id: 'goals-policy', label: '안전보건 목표와 경영방침', metricIds: [] },
  {
    id: 'hazard-improvement',
    label: '유해·위험요인 확인 및 개선',
    metricIds: [
      'risk.items',
      'risk.plans',
      'risk.actions',
      'inspection.findings',
      'inspection.actions',
    ],
  },
  { id: 'budget', label: '안전보건 예산', metricIds: [] },
  { id: 'responsible-performance', label: '안전보건관리책임자 업무수행 평가', metricIds: [] },
  { id: 'appointment-staffing', label: '안전보건 담당자 선임 및 배치', metricIds: [] },
  { id: 'worker-opinions', label: '종사자 의견 청취', metricIds: [] },
  { id: 'emergency-response', label: '비상 매뉴얼 및 대응', metricIds: ['drill.completed'] },
  { id: 'contractor-evaluation', label: '도급·용역·위탁 평가', metricIds: [] },
  { id: 'legal-obligations', label: '안전보건 관계법령 의무 점검', metricIds: [] },
  {
    id: 'education',
    label: '안전보건교육 이행',
    metricIds: ['education.records', 'education.attendance'],
  },
] as const satisfies readonly {
  id: string;
  label: string;
  metricIds: readonly HqHalfyearMetricId[];
}[];
export const hqHalfyearTopicIdSchema = z.enum(hqHalfyearTopics.map((row) => row.id));
export type HqHalfyearTopicId = z.infer<typeof hqHalfyearTopicIdSchema>;
const id = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u);
const date = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/u)
  .refine((value) => {
    const parsed = new Date(`${value}T00:00:00Z`);
    return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
  }, 'Invalid date');
const notes = z.string().max(10000);
export const hqHalfyearTopicSchema = z
  .object({
    id: hqHalfyearTopicIdSchema,
    label: z.string().trim().min(1).max(200),
    metricIds: z.array(hqHalfyearMetricIdSchema).max(hqHalfyearMetricCatalog.length),
    implementationNotes: notes,
    conclusion: notes,
    status: z.enum(['pending', 'reviewing', 'followup', 'completed']),
    responsible: z.string().max(200),
    dueDate: date.nullable(),
    sites: z
      .array(z.object({ siteId: id, evidenceNotes: notes, reviewNotes: notes }).strict())
      .max(200),
  })
  .strict();
export type HqHalfyearTopic = z.infer<typeof hqHalfyearTopicSchema>;
export const hqHalfyearPeriodSchema = z
  .object({
    year: z.number().int().min(2000).max(2200),
    half: z.union([z.literal(1), z.literal(2)]),
  })
  .strict();
export function hqHalfyearPeriod(year: number, half: 1 | 2) {
  return {
    start: `${year}-${half === 1 ? '01-01' : '07-01'}`,
    end: `${year}-${half === 1 ? '06-30' : '12-31'}`,
  };
}
export const hqHalfyearDraftSchema = z
  .object({
    ...hqHalfyearPeriodSchema.shape,
    cutoff: date,
    siteIds: z.array(id).min(1).max(200),
    topics: z.array(hqHalfyearTopicSchema).length(10),
  })
  .strict()
  .superRefine((draft, ctx) => {
    const period = hqHalfyearPeriod(draft.year, draft.half);
    if (draft.cutoff < period.start || draft.cutoff > period.end)
      ctx.addIssue({
        code: 'custom',
        path: ['cutoff'],
        message: 'Cutoff must be within selected half',
      });
    if (new Set(draft.siteIds).size !== draft.siteIds.length)
      ctx.addIssue({ code: 'custom', path: ['siteIds'], message: 'Duplicate site' });
    if (new Set(draft.topics.map((topic) => topic.id)).size !== 10)
      ctx.addIssue({
        code: 'custom',
        path: ['topics'],
        message: 'All ten stable topics are required',
      });
    for (const [index, topic] of draft.topics.entries()) {
      if (new Set(topic.metricIds).size !== topic.metricIds.length)
        ctx.addIssue({
          code: 'custom',
          path: ['topics', index, 'metricIds'],
          message: 'Duplicate metric binding',
        });
      if (
        new Set(topic.sites.map((site) => site.siteId)).size !== topic.sites.length ||
        topic.sites.some((site) => !draft.siteIds.includes(site.siteId))
      )
        ctx.addIssue({
          code: 'custom',
          path: ['topics', index, 'sites'],
          message: 'Site notes must match selected sites',
        });
    }
  });
export type HqHalfyearDraft = z.infer<typeof hqHalfyearDraftSchema>;
export function createHqHalfyearDraft(
  year: number,
  half: 1 | 2,
  siteIds: string[] = [],
  cutoff = hqHalfyearPeriod(year, half).end,
): HqHalfyearDraft {
  return {
    year,
    half,
    cutoff,
    siteIds,
    topics: hqHalfyearTopics.map((topic) => ({
      ...topic,
      metricIds: [...topic.metricIds],
      implementationNotes: '',
      conclusion: '',
      status: 'pending',
      responsible: '',
      dueDate: null,
      sites: siteIds.map((siteId) => ({ siteId, evidenceNotes: '', reviewNotes: '' })),
    })),
  };
}
export const hqHalfyearMetricValueSchema = z
  .object({
    id: hqHalfyearMetricIdSchema,
    status: z.enum(['available', 'unavailable']),
    value: z.number().int().nonnegative().nullable(),
    reason: z.string().max(300).nullable(),
    generatedAt: z.string().datetime().nullable(),
    generation: z.number().int().positive().nullable(),
    actualPeriod: z
      .object({ start: date, end: date, kind: z.enum(['annual', 'monthly']) })
      .strict()
      .nullable(),
  })
  .strict()
  .superRefine((metric, ctx) => {
    if (
      metric.status === 'available' &&
      (metric.value === null ||
        metric.generatedAt === null ||
        metric.generation === null ||
        metric.actualPeriod === null ||
        metric.reason !== null)
    )
      ctx.addIssue({ code: 'custom', message: 'Available metric requires source evidence' });
    if (metric.status === 'unavailable' && (metric.value !== null || !metric.reason))
      ctx.addIssue({ code: 'custom', message: 'Unavailable metric requires reason and no value' });
  });
export type HqHalfyearMetricValue = z.infer<typeof hqHalfyearMetricValueSchema>;
export const hqHalfyearSiteSourceSchema = z
  .object({
    siteId: id,
    name: z.string().min(1).max(200),
    capturedAt: z.string().datetime(),
    metrics: z.array(hqHalfyearMetricValueSchema).max(hqHalfyearMetricCatalog.length),
  })
  .strict();
export type HqHalfyearSiteSource = z.infer<typeof hqHalfyearSiteSourceSchema>;
export const hqHalfyearDocumentSchema = z
  .object({
    schemaVersion: z.literal(1),
    tenantId: z.literal('kop'),
    id,
    version: z.number().int().positive(),
    lifecycle: z.literal('draft'),
    draft: hqHalfyearDraftSchema,
    siteSnapshots: z.array(hqHalfyearSiteSourceSchema).max(200),
    savedAt: z.string().datetime(),
    savedBy: id,
    contentHash: z.string().regex(/^[a-f0-9]{64}$/u),
  })
  .strict();
export type HqHalfyearDocument = z.infer<typeof hqHalfyearDocumentSchema>;
export const hqHalfyearLoadResultSchema = z
  .object({
    document: hqHalfyearDocumentSchema.nullable(),
    sites: z.array(z.object({ id, name: z.string().min(1).max(200) }).strict()).max(200),
    sources: z.array(hqHalfyearSiteSourceSchema).max(200),
    canEdit: z.boolean(),
  })
  .strict();
export type HqHalfyearLoadResult = z.infer<typeof hqHalfyearLoadResultSchema>;
export const hqHalfyearSaveInputSchema = z
  .object({
    commandId: id,
    expectedVersion: z.number().int().nonnegative(),
    draft: hqHalfyearDraftSchema,
  })
  .strict();
export type HqHalfyearSaveInput = z.infer<typeof hqHalfyearSaveInputSchema>;
export const hqHalfyearSaveResultSchema = z
  .object({
    document: hqHalfyearDocumentSchema,
    receipt: z
      .object({
        commandId: id,
        version: z.number().int().positive(),
        savedAt: z.string().datetime(),
        replayed: z.boolean(),
      })
      .strict(),
  })
  .strict();
export type HqHalfyearSaveResult = z.infer<typeof hqHalfyearSaveResultSchema>;
